import { isPlatformBrowser, Location } from '@angular/common'
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Inject,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
  SimpleChanges,
  ViewChild
} from '@angular/core'
import { ActivatedRoute } from '@angular/router'
import { forkJoin, iif, Observable, of } from 'rxjs'
import { finalize, map } from 'rxjs/operators'
import { PlayerListenerComponentDirective } from '../../base-component/player-listener.component'
import { WIDGET_ID_QUERY_PARAM } from '../../constants/query-params.constants'
import { BaseGetModel } from '../../domain/base-get-model'
import { BeatsSortBy, CompilationTypes, SortBy } from '../../enums/enums'
import { convertITrackToPlayerTrack, ITrack } from '../../Interface/track'
import { PlayerLogic } from '../../player/AngPlayer'
import { CartService } from '../../services/cart.service'
import { GlobalSearchService } from '../../services/global-search.service'
import { I18nService, LgType } from '../../services/prod/i18n.service'
import { ProducerService } from '../../services/producer.service'
import { SetColorsService } from '../../services/set-colors.service'
import { SsrService } from '../../services/ssr.service'
import { TracksService } from '../../services/tracks.service'
import { WidgetsApiService } from '../../services/widget/widgets-api.service'
import { AppType } from '../../types/app.type'
import {
  FilterPropertyType,
  IdType,
  MultiSelectMode,
  PagesWithFilterType,
  SelectedTagType
} from '../../types/filter.type'

@Component({
  selector: 'bmcore-tracks-block',
  templateUrl: './bmcore-tracks-block.component.html',
  styleUrls: ['./bmcore-tracks-block.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class BmcoreTracksBlockComponent
  extends PlayerListenerComponentDirective<ITrack>
  implements OnInit, OnChanges, OnDestroy
{
  @Input() pageSize: number
  @Input() showSearchFields = true
  @Input() isTracksPage: boolean
  @Input() isShowTracksAllBeatmakers = false
  @Input() isBeatmakerPage: boolean
  @Input() producerId: number // for BMSTV to show tracks for concrete beatmaker
  @Input() pagesWithFilter: PagesWithFilterType // for BMSTV to show tracks for concrete beatmaker

  @Input() beatmakerId: number
  @Input() currentSortById: SortBy = BeatsSortBy.ChartTop

  @Input() appType: AppType

  @Input() widgetId: number
  @Input() autoplayWidgetBeats: boolean

  @Input() showVerificationIcon = false

  MAX_TRACKS_COUNT = 1000

  lg: LgType
  tracks: ITrack[] = []

  totalTracksCount: number
  currentPage = 1
  styleId: IdType = 'all'
  moodId: IdType = 'all'

  isLoader = false
  isFreeBeatsPage = false
  isBmtvOrBmstvApp = false

  searchTagId: number
  searchTagTitle = ''

  searchBeatmakerId: number = null
  searchBeatmakerTitle = ''

  searchTrack = ''

  /** расширенные фильтры каталога */
  selectedTags: SelectedTagType[] = []
  tagsMode: MultiSelectMode = 'and'
  instrumentIds: number[] = []
  instrumentsMode: MultiSelectMode = 'and'
  keyId: IdType = 'all'
  trackTypeId: IdType = 'all'
  bpmFrom: number = null
  bpmTo: number = null
  priceFrom: number = null
  priceTo: number = null

  /** названия тегов из URL до того, как по ним найдены id */
  private urlTagTitles: string[] = []

  tracksNotFound = false
  colorSettingsMode = false
  compilationTypes: typeof CompilationTypes = CompilationTypes

  promoTracks: ITrack[] = []

  hasMoreTracksToShow = false

  /**
   * Автоподгрузка треков.
   *
   * Прежняя реализация (ngx-infinite-scroll) срабатывала только по событиям
   * scroll и с внутренним троттлингом/дедупом, из-за чего:
   *  - не грузила ничего, если список короче экрана (нечего скроллить);
   *  - иногда требовала «поёрзать» скроллом у самого низа, чтобы догрузить.
   *
   * Теперь автоподгрузка завязана на геометрию маркера конца списка
   * (#loadMoreSentinel) через единую проверку maybeLoadMore(), которую дёргают
   * три независимых источника:
   *  1) IntersectionObserver — основной, эффективный триггер при скролле;
   *  2) слушатели scroll/resize (rAF-троттлинг) — запасной путь на случай сред,
   *     где IO не доставляет колбэки, и чтобы гарантированно проверить позицию
   *     в точке, где пользователь остановился;
   *  3) проверка после каждой загрузки — «дозагрузка», пока список не заполнит
   *     экран (IO не шлёт повторный колбэк, пока маркер остаётся видимым).
   */
  private readonly isBrowser: boolean
  /** за сколько пикселей до маркера начинать подгрузку (упреждающая загрузка) */
  private readonly LOAD_AHEAD_PX = 400
  private intersectionObserver: IntersectionObserver | null = null
  private sentinelEl: HTMLElement | null = null
  private scrollListenersAttached = false
  private scrollRafScheduled = false
  private readonly onScrollOrResize = () => {
    if (this.scrollRafScheduled) {
      return
    }
    this.scrollRafScheduled = true
    requestAnimationFrame(() => {
      this.scrollRafScheduled = false
      this.maybeLoadMore()
    })
  }

  @ViewChild('loadMoreSentinel')
  set loadMoreSentinel(ref: ElementRef<HTMLElement> | undefined) {
    this.observeSentinel(ref?.nativeElement ?? null)
  }

  constructor(
    private tracksService: TracksService,
    private i18nService: I18nService,
    public player: PlayerLogic,
    protected cartService: CartService,
    private setColorsService: SetColorsService,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private globalSearchService: GlobalSearchService,
    private ssrService: SsrService,
    private producerService: ProducerService,
    private readonly widgetsApiService: WidgetsApiService,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object
  ) {
    super(player, cartService)
    this.isBrowser = isPlatformBrowser(platformId)
  }

  ngOnDestroy() {
    this.intersectionObserver?.disconnect()
    this.intersectionObserver = null
    this.sentinelEl = null
    if (this.scrollListenersAttached) {
      window.removeEventListener('scroll', this.onScrollOrResize)
      window.removeEventListener('resize', this.onScrollOrResize)
      this.scrollListenersAttached = false
    }
  }

  /** (пере)подключает IntersectionObserver к маркеру и вешает scroll/resize-запас */
  private observeSentinel(el: HTMLElement | null) {
    if (!this.isBrowser) {
      return
    }

    if (typeof IntersectionObserver !== 'undefined' && !this.intersectionObserver) {
      this.intersectionObserver = new IntersectionObserver(
        entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            this.zone.run(() => this.loadMoreTracks())
          }
        },
        { root: null, rootMargin: `0px 0px ${this.LOAD_AHEAD_PX}px 0px`, threshold: 0 }
      )
    }

    // запасной путь: scroll/resize вне зоны Angular, чтобы не гонять CD на каждый пиксель
    if (!this.scrollListenersAttached) {
      this.zone.runOutsideAngular(() => {
        window.addEventListener('scroll', this.onScrollOrResize, { passive: true })
        window.addEventListener('resize', this.onScrollOrResize)
      })
      this.scrollListenersAttached = true
    }

    if (this.sentinelEl === el) {
      return
    }
    if (this.sentinelEl) {
      this.intersectionObserver?.unobserve(this.sentinelEl)
    }
    this.sentinelEl = el
    if (el) {
      this.intersectionObserver?.observe(el)
    }
  }

  /**
   * Единая проверка: если маркер конца списка находится в пределах вьюпорта
   * (+ упреждающий отступ) — грузим следующую порцию. Геометрию считаем без
   * change detection; в зону входим только когда реально нужно грузить.
   */
  private maybeLoadMore() {
    if (!this.sentinelEl || this.isLoader) {
      return
    }
    const rect = this.sentinelEl.getBoundingClientRect()
    const isSentinelWithinView = rect.top <= window.innerHeight + this.LOAD_AHEAD_PX
    if (isSentinelWithinView) {
      this.zone.run(() => this.loadMoreTracks())
    }
  }

  /**
   * После каждой загрузки ждём отрисовки добавленных треков и повторяем проверку,
   * чтобы дозаполнить экран, когда список всё ещё короче вьюпорта.
   */
  private scheduleFillViewportCheck() {
    if (!this.isBrowser) {
      return
    }
    setTimeout(() => this.maybeLoadMore(), 0)
  }

  ngOnChanges(changes: SimpleChanges) {
    this.isBmtvOrBmstvApp = this.appType === 'BMSTV' || this.appType === 'BMTV'

    if (this.appType === 'WIDGET' && changes['beatmakerId'].currentValue) {
      this.beatmakerId = changes['beatmakerId'].currentValue

      this.getTracks()
    }
  }

  ngOnInit() {
    this.isFreeBeatsPage = this.location.path().startsWith('/free-beats')

    const sortBy = this.activatedRoute.snapshot.queryParamMap.get('sortBy')
    this.currentSortById = sortBy ? Number(sortBy) : this.currentSortById

    const styleId = this.activatedRoute.snapshot.paramMap.get('styleId')
    this.styleId = styleId === 'all' || styleId === null ? 'all' : Number(styleId)

    const moodId = this.activatedRoute.snapshot.paramMap.get('moodId')
    this.moodId = moodId === 'all' || moodId === null ? 'all' : Number(moodId)

    this.searchTrack = this.activatedRoute.snapshot.queryParams['beat']
    this.searchTagTitle = this.activatedRoute.snapshot.queryParams['tag']
    this.searchBeatmakerTitle = this.activatedRoute.snapshot.queryParams['beatmaker']

    this.readCatalogFiltersFromUrl()

    this.lg = this.i18nService.getLanguageForPage()

    this.getColorSettingsMode()
    this.getInitTracks()

    // TODO
    setTimeout(() => {
      this.showPlayerInitially()
    }, 1000)

    this.producerService.producer.subscribe(res => {
      if (res) {
        this.getTracks(true)
      }
    })
  }

  /**
   * Теги в URL хранятся названиями (так уже было для одиночного тега) — id восстанавливаются
   * поиском в getInitTracks. Остальные фильтры хранятся id, их достаточно распарсить.
   */
  private readCatalogFiltersFromUrl() {
    const query = this.activatedRoute.snapshot.queryParams

    this.tagsMode = query['tagsMode'] === 'or' ? 'or' : 'and'
    this.instrumentsMode = query['instrumentsMode'] === 'or' ? 'or' : 'and'

    this.instrumentIds = this.parseIds(query['instruments'])
    this.keyId = this.parseId(query['key'])
    this.trackTypeId = this.parseId(query['trackType'])

    this.bpmFrom = this.parseNumber(query['bpmFrom'])
    this.bpmTo = this.parseNumber(query['bpmTo'])
    this.priceFrom = this.parseNumber(query['priceFrom'])
    this.priceTo = this.parseNumber(query['priceTo'])

    this.urlTagTitles = (query['tags'] ?? '')
      .split(',')
      .map((title: string) => title.trim())
      .filter((title: string) => !!title)
  }

  private parseIds(raw?: string): number[] {
    if (!raw) {
      return []
    }

    return raw
      .split(',')
      .map(part => Number(part))
      .filter(id => Number.isFinite(id) && id > 0)
  }

  private parseId(raw?: string): IdType {
    const parsed = Number(raw)

    return Number.isFinite(parsed) && parsed > 0 ? parsed : 'all'
  }

  private parseNumber(raw?: string): number | null {
    if (raw === undefined || raw === null || raw === '') {
      return null
    }

    const parsed = Number(raw)

    return Number.isFinite(parsed) && parsed >= 0 ? parsed : null
  }

  getInitTracks() {
    if (this.appType === 'BMSTV') {
      this.ssrService.wait('bmcore-tracks-block')
    }

    const hasTagsToResolve = this.showCatalogFilters ? this.urlTagTitles.length > 0 : !!this.searchTagTitle

    if (hasTagsToResolve || this.searchBeatmakerTitle) {
      forkJoin([
        this.resolveTagsFromUrl(),
        this.searchBeatmakerTitle ? this.globalSearchService.getBeatmakerIdBySearch(this.searchBeatmakerTitle) : of([])
      ]).subscribe(([tags, beatmakerResponse]) => {
        /* Tag search */
        if (this.showCatalogFilters) {
          this.selectedTags = tags
        } else if (tags.length) {
          this.searchTagId = tags[0].id
        }

        /* Beatmaker search */
        if (beatmakerResponse.length) {
          const searchBeatmaker = beatmakerResponse.find(b => b.value === this.searchBeatmakerTitle)
          this.searchBeatmakerId = searchBeatmaker?.id
        }

        this.getTracks()
      })
    } else if (this.appType !== 'WIDGET') {
      this.getTracks()
    }
  }

  /** В URL теги лежат названиями, а запросу нужны id — восстанавливаем их поиском */
  private resolveTagsFromUrl(): Observable<SelectedTagType[]> {
    const titles = this.showCatalogFilters
      ? this.urlTagTitles
      : this.searchTagTitle
        ? [this.searchTagTitle]
        : []

    if (!titles.length) {
      return of([])
    }

    return forkJoin(
      titles.map(title =>
        this.globalSearchService.getTagIdBySearch(title).pipe(
          map(response => {
            const found = response.find(item => item.value === title)

            return found ? { id: found.id, title: found.value } : null
          })
        )
      )
    ).pipe(map(tags => tags.filter(tag => !!tag)))
  }

  getBeatmakerId(): null | number {
    if (this.isShowTracksAllBeatmakers) {
      return this.searchBeatmakerId ? this.searchBeatmakerId : null
    } else if (!this.isShowTracksAllBeatmakers && this.producerId) {
      return this.producerId
    } else {
      return this.beatmakerId
    }
  }

  getStyle(): null | number {
    if (this.styleId === 'all') {
      return null
    } else {
      this.styleId = Number(this.styleId)
      return this.styleId
    }
  }

  getMood(): null | number {
    if (this.moodId === 'all') {
      return null
    } else {
      this.moodId = Number(this.moodId)
      return this.moodId
    }
  }

  getSortById(): SortBy {
    return this.currentSortById !== null ? this.currentSortById : null
  }

  getCompilationType() {
    return this.isShowTracksAllBeatmakers ? this.compilationTypes.CatalogBeats : null
  }

  /** расширенные фильтры есть только в каталоге: у фри-битов и виджета своя выдача */
  get showCatalogFilters(): boolean {
    return this.isBmtvOrBmstvApp && !this.isFreeBeatsPage && this.appType !== 'WIDGET'
  }

  get hasActiveFilters(): boolean {
    return (
      this.selectedTags.length > 0 ||
      this.instrumentIds.length > 0 ||
      this.keyId !== 'all' ||
      this.trackTypeId !== 'all' ||
      this.styleId !== 'all' ||
      this.moodId !== 'all' ||
      this.bpmFrom !== null ||
      this.bpmTo !== null ||
      this.priceFrom !== null ||
      this.priceTo !== null ||
      !!this.searchTrack ||
      !!this.searchBeatmakerId
    )
  }

  private getCatalogFilterParams() {
    if (!this.showCatalogFilters) {
      return {}
    }

    return {
      tagIds: this.selectedTags.map(tag => tag.id),
      tagsMode: this.tagsMode,
      instrumentIds: this.instrumentIds,
      instrumentsMode: this.instrumentsMode,
      keyIds: this.keyId === 'all' ? [] : [Number(this.keyId)],
      trackTypeIds: this.trackTypeId === 'all' ? [] : [Number(this.trackTypeId)],
      bpmFrom: this.bpmFrom,
      bpmTo: this.bpmTo,
      priceFrom: this.priceFrom,
      priceTo: this.priceTo
    }
  }

  private getBeatsParams() {
    return {
      beatmakerId: this.getBeatmakerId(),
      styleId: this.getStyle(),
      moodId: this.getMood(),
      sortBy: this.getSortById(),
      pageNo: this.currentPage,
      pageSize: this.pageSize,
      compilationType: this.getCompilationType(),
      beatName: this.searchTrack,
      // одиночный тег остаётся для страниц без расширенного фильтра
      tagId: this.showCatalogFilters ? null : this.searchTagId,
      ...this.getCatalogFilterParams()
    }
  }

  getTracks(isAvatarChange?: boolean) {
    this.isLoader = true

    const shouldLoadPromoTracks = this.isBmtvOrBmstvApp && this.location.path().startsWith('/catalog')

    const shouldLoadFreeBeats = this.isBmtvOrBmstvApp && this.isFreeBeatsPage

    if (shouldLoadFreeBeats) {
      this.tracksService
        .getFreeBeats(this.getStyle(), this.getMood(), this.getSortById(), this.currentPage, this.pageSize)
        .pipe(finalize(() => (this.isLoader = false)))
        .subscribe({
          next: freeBeatsResp => this.handleTrackResults([], freeBeatsResp, isAvatarChange),
          error: () => this.handleTracksError()
        })
    } else if (this.currentPage === 1 && shouldLoadPromoTracks) {
      forkJoin({
        // Эта проверка необходима для того чтобы при переключении жанров (стилей, тегов)
        // не шел один и тот же запрос за промобитами (getSpecialPromoBeats) (Каталог - топ 3)
        promoBeatsResponse: iif(
          () => !this.promoTracks.length,
          this.tracksService.getSpecialPromoBeats(),
          of({ items: [] })
        ),
        catalogTracks: this.tracksService.getBeats(this.getBeatsParams())
      })
        .pipe(finalize(() => (this.isLoader = false)))
        .subscribe({
          next: ({ promoBeatsResponse, catalogTracks }) => {
            if (promoBeatsResponse.items.length) {
              this.promoTracks = promoBeatsResponse.items
            }
            const catalogTracksWithoutPromoTracks = this.removeDuplicates(catalogTracks)
            this.handleTrackResults(this.promoTracks, catalogTracksWithoutPromoTracks, isAvatarChange)
          },
          error: () => this.handleTracksError()
        })
    } else if (this.appType === 'WIDGET' && this.beatmakerId) {
      this.loadCatalogTracks(isAvatarChange)

      // TODO: Conditional branch below is commented cause of:
      // 1) Currently, widget beats are always beats from catalog of beatmaker
      // 2) The endpoint to get beats of the widget doesn't takes necessary params
      // for bmcore-find-tracks
      //   this.widgetsApiService
      //     .getWidgetBeats({ widgetId: this.widgetId, page: this.currentPage })
      //     .subscribe(widgetBeats => {
      //       this.handleTrackResults([], widgetBeats, isAvatarChange)
      //     })
    } else {
      this.loadCatalogTracks(isAvatarChange)
    }
  }

  // Method to handle track results and update tracks array
  handleTrackResults(
    promoTracks: ITrack[],
    tracksResponse: { items: ITrack[]; count: number },
    isAvatarChange?: boolean
  ) {
    this.ssrService.ready('bmcore-tracks-block')
    this.isLoader = false

    // This logic sets 6th promo track isPromo status to false on beatmaker catalog
    const beatmakerCatalogRegexp = /^\/bm\/\d+\/catalog/
    const isBeatmakerCatalog = beatmakerCatalogRegexp.test(this.location.path())
    if (isBeatmakerCatalog) {
      tracksResponse = this.changeSixthPromoTrackStatus(tracksResponse)
    }

    // Combine promo and catalog tracks if there're promo tracks
    if (promoTracks.length) {
      this.tracks = [...promoTracks, ...tracksResponse.items]
    } else {
      // Append new tracks to the end of the tracks array
      if (isAvatarChange) {
        this.tracks = tracksResponse.items
      } else {
        this.tracks.splice(this.tracks.length, 0, ...tracksResponse.items)
      }
    }

    // Track not found message
    this.totalTracksCount = tracksResponse.count
    if (this.totalTracksCount === 0) {
      this.tracksNotFound = true
    }

    this.hasMoreTracksToShow = this.totalTracksCount > this.tracks.length

    this.setPlaylist()

    // после отрисовки порции проверяем, не нужно ли догрузить, чтобы заполнить экран
    if (this.isTracksPage) {
      this.scheduleFillViewportCheck()
    }
  }

  // Method to load catalog tracks and append them to tracks array
  loadCatalogTracks(isAvatarChange?: boolean) {
    this.tracksService
      .getBeats(this.getBeatsParams())
      .pipe(finalize(() => (this.isLoader = false)))
      .subscribe({
        next: res => {
          if (this.isBmtvOrBmstvApp) {
            const catalogTracksWithoutPromoTracks = this.removeDuplicates(res)
            this.handleTrackResults([], catalogTracksWithoutPromoTracks, isAvatarChange)
          } else {
            this.handleTrackResults([], res, isAvatarChange)
          }
        },
        error: () => this.handleTracksError()
      })
  }

  // Обработка ошибки загрузки треков: снимаем состояние SSR и откатываем номер
  // страницы, увеличенный в loadMoreTracks() до запроса, чтобы неудавшуюся
  // страницу можно было запросить повторно (флаг isLoader снимается в finalize).
  private handleTracksError() {
    this.ssrService.ready('bmcore-tracks-block')
    if (this.currentPage > 1) {
      this.currentPage--
    }
  }

  loadMoreTracks() {
    // защита от параллельных/повторных загрузок (IO + проверка заполнения экрана)
    if (this.isLoader) {
      return
    }

    const hasReachedMaxTracks = this.tracks.length >= this.MAX_TRACKS_COUNT
    const hasMorePagesToLoad = this.currentPage < Math.ceil(this.totalTracksCount / this.pageSize)

    const shouldLoadMoreTracks = !hasReachedMaxTracks && hasMorePagesToLoad

    if (shouldLoadMoreTracks) {
      this.currentPage++
      this.getTracks()
    }
  }

  filterTracks(value: FilterPropertyType) {
    switch (value.title) {
      case 'sort-top-new':
        this.currentSortById = value.sort
        break
      case 'style':
        this.styleId = value.id
        break
      case 'mood':
        this.moodId = value.id
        break
      case 'name':
        this.searchTrack = value.search
        break
      case 'tag':
        this.searchTagId = value.id as number
        this.searchTagTitle = value.search
        break
      case 'beatmaker':
        this.searchBeatmakerId = value.id as number
        this.searchBeatmakerTitle = value.search
        break
      case 'tags':
        this.applySelectedTags(value.ids ?? [], value.search)
        this.tagsMode = value.mode ?? this.tagsMode
        break
      case 'instruments':
        this.instrumentIds = value.ids ?? []
        this.instrumentsMode = value.mode ?? this.instrumentsMode
        break
      case 'key':
        this.keyId = value.id
        break
      case 'track-type':
        this.trackTypeId = value.id
        break
      case 'bpm':
        this.bpmFrom = value.from
        this.bpmTo = value.to
        break
      case 'price':
        this.priceFrom = value.from
        this.priceTo = value.to
        break
      case 'clear':
        this.resetFilters()
        break
    }

    /* После изменения настроек фильтра, сбросить следующие настройки к первоначальным */
    this.currentPage = 1
    this.tracksNotFound = false
    this.tracks = []

    this.getTracks()
    this.refreshUrl()
  }

  /**
   * Держим и id, и название тега: id нужен запросу, название — чипу и URL.
   * Названия известны только для тегов, добавленных через поиск.
   */
  private applySelectedTags(ids: number[], addedTitle?: string) {
    const knownTitles = new Map(this.selectedTags.map(tag => [tag.id, tag.title]))

    this.selectedTags = ids.map(id => ({
      id,
      title: knownTitles.get(id) ?? addedTitle ?? String(id)
    }))
  }

  private resetFilters() {
    this.selectedTags = []
    this.tagsMode = 'and'
    this.instrumentIds = []
    this.instrumentsMode = 'and'
    this.keyId = 'all'
    this.trackTypeId = 'all'
    this.styleId = 'all'
    this.moodId = 'all'
    this.bpmFrom = null
    this.bpmTo = null
    this.priceFrom = null
    this.priceTo = null
    this.searchTrack = ''
    this.searchTagId = null
    this.searchTagTitle = ''
    this.searchBeatmakerId = null
    this.searchBeatmakerTitle = ''
  }

  getPageRoute() {
    const currentPath = this.location.path()

    const routesMap = {
      '/free-beats': 'free-beats',
      '/catalog': 'catalog'
    }

    for (const route in routesMap) {
      if (currentPath.startsWith(route)) {
        return routesMap[route]
      }
    }

    return this.appType === 'WIDGET' ? '' : 'catalog'
  }

  // https://stackoverflow.com/questions/35618463/change-route-params-without-reloading-in-angular-2
  // https://www.digitalocean.com/community/tutorials/angular-location-service
  // https://www.positronx.io/angular-location-service/
  refreshUrl() {
    let url = ''
    const pageRoute = this.getPageRoute()

    switch (this.pagesWithFilter) {
      case 'production-mode':
        url = `${pageRoute}${this.getUriParams()}${this.getQueryParams('?')}`
        break

      case 'dev-mode':
        // todo: temporary solution
        const queryParamForDev = `?devBeatmakerId=${this.beatmakerId}${this.getQueryParams('&')}`
        url = `${pageRoute}${this.getUriParams()}${queryParamForDev}`
        break

      case 'widget-mode':
        const queryParamForWidget = `?${WIDGET_ID_QUERY_PARAM}=${this.widgetId}${this.getQueryParams('&')}`
        url = `${pageRoute}${this.getUriParams()}${queryParamForWidget}`
        break

      case 'bmstv-beatmaker-page':
        const beatmakerIdOrUrl = this.activatedRoute.snapshot.paramMap.get('beatmakerIdOrURL')
        url = `bm/${beatmakerIdOrUrl}/catalog${this.getUriParams()}${this.getQueryParams('?')}`
        break

      case 'no-filter':
        break
    }
    this.location.replaceState(url)
  }

  getUriParams() {
    const style = this.getStyle() ? this.getStyle() : 'all'
    const mood = this.getMood() ? this.getMood() : 'all'

    const result = `/${style}/${mood}`
    return result === '/all/all' ? '' : result
  }

  getQueryParams(sign: string) {
    // TODO: the server does not respond to changes
    const parts = [`sortBy=${this.currentSortById}`]

    // значения обязательно кодируем: в названиях битов и тегов встречаются пробелы и &
    const add = (name: string, value: string | number) => parts.push(`${name}=${encodeURIComponent(value)}`)

    if (this.searchTrack) {
      add('beat', this.searchTrack)
    }

    if (this.searchBeatmakerTitle) {
      add('beatmaker', this.searchBeatmakerTitle)
    }

    if (this.showCatalogFilters) {
      if (this.selectedTags.length) {
        add('tags', this.selectedTags.map(tag => tag.title).join(','))

        if (this.selectedTags.length > 1) {
          add('tagsMode', this.tagsMode)
        }
      }

      if (this.instrumentIds.length) {
        add('instruments', this.instrumentIds.join(','))

        if (this.instrumentIds.length > 1) {
          add('instrumentsMode', this.instrumentsMode)
        }
      }

      if (this.keyId !== 'all') {
        add('key', this.keyId)
      }

      if (this.trackTypeId !== 'all') {
        add('trackType', this.trackTypeId)
      }

      if (this.bpmFrom !== null) {
        add('bpmFrom', this.bpmFrom)
      }

      if (this.bpmTo !== null) {
        add('bpmTo', this.bpmTo)
      }

      if (this.priceFrom !== null) {
        add('priceFrom', this.priceFrom)
      }

      if (this.priceTo !== null) {
        add('priceTo', this.priceTo)
      }
    } else if (this.searchTagId) {
      add('tag', this.searchTagTitle)
    }

    return sign + parts.join('&')
  }

  showPlayerInitially() {
    if (this.appType === 'WIDGET' || this.appType === 'PROD-PAGE') {
      if (this.tracks.length) {
        this.player.setTrack(convertITrackToPlayerTrack(this.tracks[0]), this.tracks[0])
      }
    } else if (this.isBmtvOrBmstvApp) {
      // Логика вызова рекламного бита запускается на бекенде
    }
  }

  setPlaylist() {
    this.player.setPlayList(this.tracks.map(convertITrackToPlayerTrack))
  }

  getColorSettingsMode() {
    this.setColorsService.colorSettingsSubject.subscribe(res => {
      this.colorSettingsMode = res
    })
  }

  removeDuplicates(catalogTracks: BaseGetModel<ITrack>) {
    const promoTrackIds = this.promoTracks.map(track => track.id)
    const filteredTracks = catalogTracks.items.filter(track => !promoTrackIds.includes(track.id))

    return {
      count: catalogTracks.count,
      items: filteredTracks
    }
  }

  changeSixthPromoTrackStatus(catalogTracks: BaseGetModel<ITrack>) {
    const mappedTracks = catalogTracks.items.map(track => {
      return track.isPromo ? { ...track, isPromo: false } : track
    })

    return {
      count: catalogTracks.count,
      items: mappedTracks
    }
  }

  checkToShowFavBtn() {
    return this.isBmtvOrBmstvApp && !this.isFreeBeatsPage
  }
}
